/**
 * Batch-12 — Verordnungsmanagement (Drawer-Inhalt der PB-Jahresübersicht).
 *
 * Liste aller Verordnungen des PB + Anlegen/Bearbeiten/Löschen.
 * Eine Verordnung hat Titel, Termin und optional eine Erinnerung
 * („X Tage vorher erinnern") sowie eine Beschreibung.
 */
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, getThemeColors } from '../../../../lib/theme';
import { v2Verordnungen, type Verordnung } from '../../../../lib/v1';
import { toast } from '../../../../lib/toast';
import { formatDate } from '../../../../lib/dateFormat';
import { DateInput } from '../../../../components/DateInput';

interface Props {
  dark: boolean;
  userId: number;
  verordnungen: Verordnung[];
  /** Nach Anlegen/Bearbeiten/Löschen — Parent lädt die Liste neu. */
  onChanged: () => void;
  /** Vorausgewählte Verordnung (Klick auf Timeline-Marker) — öffnet den Editor. */
  editId?: number | null;
}

interface FormState {
  titel: string;
  termin: string;
  erinnerung_tage: string;
  beschreibung: string;
}

const EMPTY_FORM: FormState = { titel: '', termin: '', erinnerung_tage: '', beschreibung: '' };

function toForm(v: Verordnung): FormState {
  return {
    titel: v.titel ?? '',
    termin: (v.termin ?? '').slice(0, 10),
    erinnerung_tage: v.erinnerung_tage != null ? String(v.erinnerung_tage) : '',
    beschreibung: v.beschreibung ?? '',
  };
}

export function VerordnungenPanel({ dark, userId, verordnungen, onChanged, editId }: Props) {
  const tc = getThemeColors(dark);
  const { t: tr } = useTranslation();
  // 'new' | Verordnungs-ID | null (nur Liste)
  const [editing, setEditing] = useState<'new' | number | null>(editId ?? null);
  const [form, setForm] = useState<FormState>(() => {
    const initial = editId != null ? verordnungen.find(v => v.id === editId) : null;
    return initial ? toForm(initial) : EMPTY_FORM;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startNew = () => { setEditing('new'); setForm(EMPTY_FORM); setError(null); };
  const startEdit = (v: Verordnung) => { setEditing(v.id); setForm(toForm(v)); setError(null); };
  const cancel = () => { setEditing(null); setError(null); };

  const save = useCallback(async () => {
    if (busy) return;
    if (!form.titel.trim()) { setError(tr('pflege.verordnungen.titel_required')); return; }
    if (!form.termin) { setError(tr('pflege.verordnungen.termin_required')); return; }
    setBusy(true);
    setError(null);
    const payload = {
      titel: form.titel.trim(),
      termin: form.termin,
      erinnerung_tage: form.erinnerung_tage !== '' ? Number(form.erinnerung_tage) : null,
      beschreibung: form.beschreibung.trim() || null,
    };
    try {
      if (editing === 'new') {
        await v2Verordnungen.create(userId, payload);
      } else if (typeof editing === 'number') {
        await v2Verordnungen.update(editing, payload);
      }
      toast(tr('pflege.verordnungen.saved'), 'success');
      setEditing(null);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('pflege.edit.save_failed'));
    } finally {
      setBusy(false);
    }
  }, [busy, form, editing, userId, onChanged, tr]);

  const remove = useCallback(async (id: number) => {
    if (busy) return;
    setBusy(true);
    try {
      await v2Verordnungen.remove(id);
      toast(tr('pflege.verordnungen.deleted'), 'success');
      if (editing === id) setEditing(null);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('pflege.edit.save_failed'));
    } finally {
      setBusy(false);
    }
  }, [busy, editing, onChanged, tr]);

  const inputStyle: React.CSSProperties = {
    fontSize: 13, padding: '8px 12px', width: '100%', boxSizing: 'border-box',
    border: `1px solid ${tc.border}`, borderRadius: 8, background: tc.inputBg,
    color: tc.text, outline: 'none', fontFamily: 'Outfit,sans-serif',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, color: tc.muted, display: 'block', marginBottom: 5,
  };

  const fmtDate = (v: string) => formatDate(v, v);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Liste */}
      {verordnungen.length === 0 && editing === null && (
        <div style={{ fontSize: 13, color: tc.muted }}>{tr('pflege.verordnungen.empty')}</div>
      )}
      {verordnungen.map(v => (
        <div key={v.id} style={{ border: `1px solid ${tc.borderSoft}`, borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: tc.heading }}>{v.titel}</div>
            <div style={{ fontSize: 12, color: tc.muted, marginTop: 2 }}>
              {tr('pflege.verordnungen.termin')}: {fmtDate(v.termin)}
              {v.erinnerung_tage != null && v.erinnerung_tage > 0 && (
                <> · {tr('pflege.verordnungen.reminder_days', { count: v.erinnerung_tage })}</>
              )}
            </div>
            {v.beschreibung && (
              <div style={{ fontSize: 12, color: tc.text, marginTop: 4, whiteSpace: 'pre-wrap' }}>{v.beschreibung}</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => startEdit(v)}
              title={tr('common.action.edit')}
              style={{ background: 'none', border: `1px solid ${tc.border}`, borderRadius: 7, padding: '4px 8px', cursor: 'pointer', color: tc.muted, fontSize: 12 }}
            >✎</button>
            <button
              type="button"
              onClick={() => void remove(v.id)}
              title={tr('common.action.delete')}
              style={{ background: 'none', border: `1px solid ${tc.border}`, borderRadius: 7, padding: '4px 8px', cursor: 'pointer', color: '#b42318', fontSize: 12 }}
            >✕</button>
          </div>
        </div>
      ))}

      {/* Formular (neu/bearbeiten) */}
      {editing !== null ? (
        <div style={{ border: `1px solid ${tc.border}`, borderRadius: 10, padding: 14, background: tc.hover, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: tc.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {editing === 'new' ? tr('pflege.verordnungen.add') : tr('pflege.verordnungen.edit')}
          </div>
          <div>
            <label style={labelStyle}>{tr('pflege.verordnungen.titel')} *</label>
            <input value={form.titel} onChange={e => setForm(f => ({ ...f, titel: e.target.value }))} style={inputStyle} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>{tr('pflege.verordnungen.termin')} *</label>
              <DateInput ariaLabel={tr('pflege.verordnungen.termin')} value={form.termin} onChange={termin => setForm(f => ({ ...f, termin }))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>{tr('pflege.verordnungen.erinnerung_tage')}</label>
              <input
                type="number" min={0} max={365} placeholder="z.B. 7"
                value={form.erinnerung_tage}
                onChange={e => setForm(f => ({ ...f, erinnerung_tage: e.target.value }))}
                style={inputStyle}
              />
            </div>
          </div>
          <div>
            <label style={labelStyle}>{tr('pflege.verordnungen.beschreibung')}</label>
            <textarea
              rows={2}
              value={form.beschreibung}
              onChange={e => setForm(f => ({ ...f, beschreibung: e.target.value }))}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
          {error && <div style={{ fontSize: 12.5, color: '#b42318' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button" onClick={() => void save()} disabled={busy}
              style={{ flex: 1, fontSize: 13, fontWeight: 600, background: BRAND, color: '#fff', border: 'none', borderRadius: 7, padding: '8px 0', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}
            >
              {busy ? '…' : tr('common.action.save')}
            </button>
            <button
              type="button" onClick={cancel}
              style={{ fontSize: 13, padding: '8px 14px', background: 'none', border: `1px solid ${tc.border}`, borderRadius: 7, color: tc.muted, cursor: 'pointer' }}
            >
              {tr('common.action.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button" onClick={startNew}
          style={{ fontSize: 13, fontWeight: 600, padding: '9px 12px', background: 'transparent', border: `1px dashed ${tc.border}`, borderRadius: 8, color: BRAND, cursor: 'pointer', fontFamily: 'Outfit,sans-serif' }}
        >
          + {tr('pflege.verordnungen.add')}
        </button>
      )}
    </div>
  );
}
